import { MessagingService } from "@app/core/messaging/messaging.service";
import { Injectable } from "@angular/core";
import { Platform } from "@ionic/angular";
import { EnvironmentType } from "@app/models/env";
import { environment } from "../../environments/environment";
import {
  getSalesforceConfig,
  getSalesforceNativeProfileLabel,
  getSalesforceWebTransport,
  SALESFORCE_SDK_CONFIG,
  SALESFORCE_WEB_CONFIG,
} from "@app/config/salesforce.config";
import { formatSalesforceError, maskSalesforceSecret, sfLog } from "@app/core/salesforce/salesforce-debug.logger";
import { SalesforceWebEventsService } from "./salesforce-web-events.service";
import { SalesforceWebAttributes } from "./salesforce-web.types";

/** Subconjunto tipado da API cordova-plugin-marketingcloudsdk v8.x */
interface McCordovaPluginApi {
  enablePush(success?: () => void, error?: (err: unknown) => void): void;
  disablePush(success?: () => void, error?: (err: unknown) => void): void;
  enableLogging(success?: () => void, error?: (err: unknown) => void): void;
  setAnalyticsEnabled(enabled: boolean, success?: () => void, error?: (err: unknown) => void): void;
  setContactKey(key: string, success?: () => void, error?: (err: unknown) => void): void;
  getContactKey(success: (key: string) => void, error?: (err: unknown) => void): void;
  getAttributes(success: (attributes: Record<string, string>) => void, error?: (err: unknown) => void): void;
  setAttribute(key: string, value: string, success?: () => void, error?: (err: unknown) => void): void;
  clearAttribute(key: string, success?: () => void, error?: (err: unknown) => void): void;
  addTag(tag: string, success?: () => void, error?: (err: unknown) => void): void;
  removeTag(tag: string, success?: () => void, error?: (err: unknown) => void): void;
  getTags(success: (tags: string[]) => void, error?: (err: unknown) => void): void;
  getSystemToken(success: (token: string) => void, error?: (err: unknown) => void): void;
  getDeviceId(success: (id: string) => void, error?: (err: unknown) => void): void;
  isPushEnabled(success: (enabled: boolean) => void, error?: (err: unknown) => void): void;
  logSdkState(success?: () => void, error?: (err: unknown) => void): void;
  track(event: { objType: "CustomEvent"; name: string; attributes: Record<string, string> }): void;
}

declare const MCCordovaPlugin: McCordovaPluginApi | undefined;

interface ContactAttributes {
  [key: string]: string | number | boolean;
}

/**
 * SF-NATIVE: Mobile Push SDK (Cordova v8+) + SF-WEB: fallback no browser.
 *
 * - Android/iOS: credenciais no config nativo (capacitor.config.ts → config.xml);
 *   MCInitProvider inicializa o SDK antes do JS. JS só habilita push/analytics/logging.
 * - Web: initializeWeb() → SalesforceWebEventsService (backend | direct | log-only)
 *
 * Mapa: docs/SALESFORCE_INTEGRATION.md | Tags: SF-NATIVE, SF-WEB-*
 */
@Injectable({
  providedIn: "root",
})
export class SalesforceMarketingCloudService {
  private isInitialized = false;
  private isWebMode = false;
  private currentContactKey: string | null = null;
  private pendingWebAttributes: SalesforceWebAttributes = {};
  private initInFlight: Promise<boolean> | null = null;

  constructor(
    private platform: Platform,
    private webEvents: SalesforceWebEventsService,
    private messaging: MessagingService
  ) {}

  /**
   * Inicializa o Salesforce Marketing Cloud SDK.
   * Deve ser chamado no início do app, após platform.ready().
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      sfLog.info("SDK já inicializado");
      return true;
    }

    // Single-flight: `isInitialized` só vira true no FIM de initializeNative(),
    // depois de delayRegistration + waitForNativePlugin (~1s). Sem esta trava,
    // duas chamadas concorrentes passam pelo guard acima e o SDK é inicializado
    // (e enablePush executado) duas vezes. Acontece de verdade no startup:
    // Startup and user synchronization may initialize concurrently.
    if (this.initInFlight) {
      sfLog.info("Inicialização já em andamento — reaproveitando a chamada em curso");
      return this.initInFlight;
    }

    this.initInFlight = this.runInitialize();
    try {
      return await this.initInFlight;
    } finally {
      this.initInFlight = null;
    }
  }

  private runInitialize(): Promise<boolean> {
    // iOS/Android: nunca cair em modo web só porque o plugin Cordova ainda não injetou MCCordovaPlugin.
    if (this.isNativePlatform()) {
      return this.initializeNative();
    }

    if (this.shouldUseWebTransport()) {
      return this.initializeWeb();
    }

    sfLog.warn("SDK indisponível nesta plataforma");
    return Promise.resolve(false);
  }

  private async initializeNative(): Promise<boolean> {
    try {
      const config = getSalesforceConfig();
      const perfil = environment === EnvironmentType.production ? "production" : "development";

      sfLog.info("Inicializando SDK nativo (v8)...", {
        perfil,
        sfmcApp: getSalesforceNativeProfileLabel(),
        appId: config.appId,
        accessToken: maskSalesforceSecret(config.accessToken),
        endpoint: config.appEndpoint,
        plataforma: this.platform.platforms().join(", "),
      });

      await this.delay(SALESFORCE_SDK_CONFIG.delayRegistration);
      await this.waitForNativePlugin();

      const plugin = this.getMcPlugin();
      if (!plugin) {
        sfLog.warn("MCCordovaPlugin indisponível após deviceready — SFMC nativo desabilitado nesta sessão");
        return false;
      }

      await this.setNativeAnalytics(plugin, SALESFORCE_SDK_CONFIG.analyticsEnabled);

      const osPermissionGranted = await this.requestOsPushPermission();
      if (!osPermissionGranted) {
        sfLog.warn(
          "Permissão de notificação do SO negada ou pendente — SFMC marcará o device como not opted in"
        );
      }

      await this.enablePush(plugin);

      if (SALESFORCE_SDK_CONFIG.enableLogging) {
        await this.enableLogging(plugin);
      }

      this.isInitialized = true;
      this.isWebMode = false;
      await this.logNativeDiagnosticSummary(config, perfil);
      return true;
    } catch (error) {
      sfLog.error("Falha ao inicializar SDK nativo", error, {
        pluginDisponivel: this.getMcPlugin() !== null,
        plataforma: this.platform.platforms(),
      });
      return false;
    }
  }

  async disablePush(): Promise<boolean> {
    if (!this.isInitialized || this.isWebMode) {
      sfLog.warn("disablePush ignorado: SDK não inicializado ou modo web");
      return false;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.disablePush(() => resolve(true), () => resolve(false)),
      false
    );
  }

  async setContactKey(contactKey: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.isInitialized) {
      return false;
    }

    if (this.isWebMode) {
      this.currentContactKey = contactKey;
      if (SALESFORCE_WEB_CONFIG.contactSyncPath) {
        await this.webEvents.syncContact(contactKey, { ...this.pendingWebAttributes });
      }
      sfLog.ok("Contact key definido (web)", { contactKey });
      return true;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) =>
        plugin.setContactKey(
          contactKey,
          () => {
            this.currentContactKey = contactKey;
            sfLog.ok("Contact key definido", { contactKey });
            resolve(true);
          },
          (error) => {
            sfLog.error("Falha ao definir contact key", error, { contactKey });
            resolve(false);
          }
        ),
      false
    );
  }

  async getContactKey(): Promise<string | null> {
    if (!this.isInitialized || this.isWebMode) {
      return this.isWebMode ? this.currentContactKey : null;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      return null;
    }

    return this.callMcPlugin(
      (resolve) =>
        plugin.getContactKey(
          (contactKey) => {
            this.currentContactKey = contactKey;
            resolve(contactKey);
          },
          () => resolve(null)
        ),
      null
    );
  }

  async setAttributes(attributes: ContactAttributes): Promise<boolean> {
    if (!this.isInitialized) {
      sfLog.warn("setAttributes ignorado: SDK não inicializado");
      return false;
    }

    if (this.isWebMode) {
      Object.assign(this.pendingWebAttributes, this.toWebAttributes(attributes));
      if (this.currentContactKey && SALESFORCE_WEB_CONFIG.contactSyncPath) {
        await this.webEvents.syncContact(this.currentContactKey, { ...this.pendingWebAttributes });
      }
      return true;
    }

    try {
      const results = await Promise.all(
        Object.entries(attributes).map(([key, value]) => this.setAttribute(key, value))
      );
      return results.every(Boolean);
    } catch (error) {
      sfLog.error("Erro ao definir atributos do contato", error, attributes);
      return false;
    }
  }

  async setAttribute(key: string, value: string | number | boolean): Promise<boolean> {
    if (this.isWebMode) {
      this.pendingWebAttributes[key] = String(value);
      return true;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) =>
        plugin.setAttribute(key, String(value), () => resolve(true), (error) => {
          sfLog.error(`Falha ao definir atributo ${key}`, error, { value: String(value) });
          resolve(false);
        }),
      false
    );
  }

  async getAttribute(key: string): Promise<string | null> {
    const attributes = await this.getAttributes();
    return attributes[key] ?? null;
  }

  async getAttributes(): Promise<Record<string, string>> {
    if (!this.isInitialized || this.isWebMode) {
      return this.isWebMode ? this.toNativeEventAttributes(this.pendingWebAttributes as ContactAttributes) : {};
    }

    const plugin = this.getMcPlugin();
    if (!plugin || typeof plugin.getAttributes !== "function") {
      return {};
    }

    return this.callMcPlugin(
      (resolve) =>
        plugin.getAttributes(
          (attributes) => resolve(attributes ?? {}),
          (error) => {
            sfLog.warn("Falha ao ler atributos do SDK", error);
            resolve({});
          }
        ),
      {}
    );
  }

  async clearAttribute(key: string): Promise<boolean> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.clearAttribute(key, () => resolve(true), () => resolve(false)),
      false
    );
  }

  async addTag(tag: string): Promise<boolean> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.addTag(tag, () => resolve(true), () => resolve(false)),
      false
    );
  }

  async removeTag(tag: string): Promise<boolean> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.removeTag(tag, () => resolve(true), () => resolve(false)),
      false
    );
  }

  async getTags(): Promise<string[]> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return [];
    }

    return this.callMcPlugin(
      (resolve) => plugin.getTags((tags) => resolve(tags ?? []), () => resolve([])),
      []
    );
  }

  async getSystemToken(): Promise<string | null> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return null;
    }

    return this.callMcPlugin(
      (resolve) => plugin.getSystemToken((token) => resolve(token), () => resolve(null)),
      null
    );
  }

  async getDeviceId(): Promise<string | null> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode) {
      return null;
    }

    return this.callMcPlugin(
      (resolve) => plugin.getDeviceId((deviceId) => resolve(deviceId), () => resolve(null)),
      null
    );
  }

  async trackEvent(eventName: string, attributes?: ContactAttributes): Promise<boolean> {
    if (!SALESFORCE_SDK_CONFIG.analyticsEnabled) {
      sfLog.warn(`Evento "${eventName}" não enviado: analytics desabilitado`);
      return false;
    }

    if (!this.isInitialized) {
      sfLog.warn(`Evento "${eventName}" não enviado: SDK não inicializado`);
      return false;
    }

    if (this.isWebMode) {
      const merged = {
        ...this.pendingWebAttributes,
        ...this.toWebAttributes(attributes ?? {}),
      };
      const ok = await this.webEvents.trackEvent(eventName, merged);
      if (ok) {
        sfLog.ok(`Evento web enviado: ${eventName}`, merged);
      } else {
        sfLog.error(`Falha ao enviar evento web: ${eventName}`, undefined, merged);
      }
      return ok;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      sfLog.error(`Evento "${eventName}" não enviado: plugin nativo indisponível`);
      return false;
    }

    const nativeAttributes = this.toNativeEventAttributes(attributes ?? {});

    try {
      plugin.track({
        objType: "CustomEvent",
        name: eventName,
        attributes: nativeAttributes,
      });
      sfLog.ok(`Evento nativo registrado: ${eventName}`, {
        contactKey: this.currentContactKey ?? "(não definido)",
        atributos: nativeAttributes,
      });
      return true;
    } catch (error) {
      sfLog.error(`Falha ao registrar evento nativo: ${eventName}`, error, nativeAttributes);
      return false;
    }
  }

  async isPushEnabled(): Promise<boolean> {
    if (!this.isInitialized || this.isWebMode) {
      return false;
    }

    const plugin = this.getMcPlugin();
    if (!plugin) {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.isPushEnabled((enabled) => resolve(Boolean(enabled)), () => resolve(false)),
      false
    );
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  isWebTransport(): boolean {
    return this.isWebMode;
  }

  async getSDKState(): Promise<{
    initialized: boolean;
    contactKey: string | null;
    pushEnabled: boolean;
    deviceId?: string | null;
    tags?: string[];
  }> {
    if (!this.isInitialized) {
      return {
        initialized: false,
        contactKey: null,
        pushEnabled: false,
      };
    }

    const contactKey = await this.getContactKey();
    const pushEnabled = await this.isPushEnabled();

    if (this.isWebMode) {
      return {
        initialized: true,
        contactKey,
        pushEnabled: false,
      };
    }

    const deviceId = await this.getDeviceId();
    const tags = await this.getTags();

    return {
      initialized: true,
      contactKey,
      pushEnabled,
      deviceId,
      tags,
    };
  }

  private async setNativeAnalytics(plugin: McCordovaPluginApi, enabled: boolean): Promise<void> {
    if (typeof plugin.setAnalyticsEnabled !== "function") {
      return;
    }

    await this.callMcPlugin<void>(
      (resolve) => plugin.setAnalyticsEnabled(enabled, () => resolve(), () => resolve()),
      undefined,
      true
    );
  }

  /** The app messaging service owns the prompt; marketing only reads its OS state. */
  private async requestOsPushPermission(): Promise<boolean> {
    return (await this.messaging.checkPermission()) === "granted";
  }

  private async getOsPushPermissionGranted(): Promise<boolean> {
    return (await this.messaging.checkPermission()) === "granted";
  }

  private async queryNativePushEnabled(plugin: McCordovaPluginApi): Promise<boolean> {
    if (typeof plugin.isPushEnabled !== "function") {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.isPushEnabled((enabled) => resolve(Boolean(enabled)), () => resolve(false)),
      false
    );
  }

  private async enablePush(plugin: McCordovaPluginApi): Promise<void> {
    const pushBefore = await this.queryNativePushEnabled(plugin);
    sfLog.info("Chamando MCCordovaPlugin.enablePush()", { pushEnabledAntes: pushBefore });

    let enablePushSucceeded = false;

    await this.callMcPlugin<void>(
      (resolve) =>
        plugin.enablePush(
          () => {
            enablePushSucceeded = true;
            sfLog.ok("MCCordovaPlugin.enablePush() — callback success");
            resolve();
          },
          (error) => {
            sfLog.warn("MCCordovaPlugin.enablePush() — callback error (não bloqueia SDK)", error);
            resolve();
          }
        ),
      undefined,
      true
    );

    const pushAfter = await this.queryNativePushEnabled(plugin);
    sfLog.info("Resultado enablePush", {
      nativeCallback: enablePushSucceeded ? "success" : "error",
      pushEnabledAntes: pushBefore,
      pushEnabledDepois: pushAfter,
    });

    if (enablePushSucceeded && !pushAfter) {
      sfLog.warn(
        "enablePush retornou success mas isPushEnabled=false — verifique permissão do SO (not opted in no painel SFMC)"
      );
    }
  }

  private async enableLogging(plugin: McCordovaPluginApi): Promise<void> {
    await this.callMcPlugin<void>(
      (resolve) =>
        plugin.enableLogging(
          () => {
            sfLog.info("Logging verbose do SDK habilitado");
            resolve();
          },
          (error) => {
            sfLog.warn("Não foi possível habilitar logging verbose do SDK", error);
            resolve();
          }
        ),
      undefined,
      true
    );
  }

  private isNativePlatform(): boolean {
    return this.platform.is("capacitor") || this.platform.is("cordova");
  }

  private isNativeSdkAvailable(): boolean {
    return this.isNativePlatform() && this.getMcPlugin() !== null;
  }

  private shouldUseWebTransport(): boolean {
    if (this.isNativePlatform()) {
      return false;
    }
    return SALESFORCE_WEB_CONFIG.enabled;
  }

  private getMcPlugin(): McCordovaPluginApi | null {
    if (typeof MCCordovaPlugin === "undefined" || MCCordovaPlugin === null) {
      return null;
    }
    if (typeof MCCordovaPlugin.enablePush !== "function") {
      return null;
    }
    return MCCordovaPlugin;
  }

  private async initializeWeb(): Promise<boolean> {
    if (this.isInitialized && this.isWebMode) {
      sfLog.info("Transporte web já ativo");
      return true;
    }

    this.isWebMode = true;
    this.isInitialized = true;
    const transport = getSalesforceWebTransport();
    const label =
      transport === "log-only"
        ? "apenas console (sem envio ao SFMC — use Android/iOS para teste real)"
        : transport === "direct"
          ? "REST SFMC direto (ionic serve + proxy)"
          : `API Supermup (${SALESFORCE_WEB_CONFIG.eventsPath})`;

    sfLog.ok("SDK web inicializado", { transporte: transport, descricao: label });
    return true;
  }

  /**
   * Dump completo do estado do SDK no log nativo (Xcode / logcat).
   * É a única forma de ver o device token que o SFMC realmente registrou e o
   * payload de registro enviado ao MobilePush — indispensável para diagnosticar
   * "opted in mas o push não chega". Não expõe nada na WebView.
   */
  async logNativeSdkState(): Promise<boolean> {
    const plugin = this.getMcPlugin();
    if (!plugin || this.isWebMode || typeof plugin.logSdkState !== "function") {
      return false;
    }

    return this.callMcPlugin(
      (resolve) => plugin.logSdkState(() => resolve(true), () => resolve(false)),
      false,
      true
    );
  }

  private async logNativeDiagnosticSummary(
    config: ReturnType<typeof getSalesforceConfig>,
    perfil: string
  ): Promise<void> {
    const state = await this.getSDKState();
    const systemToken = await this.getSystemToken();
    const osNotificationGranted = await this.getOsPushPermissionGranted();

    sfLog.ok("SDK nativo pronto — diagnóstico", {
      perfil,
      sfmcApp: getSalesforceNativeProfileLabel(),
      modo: "nativo",
      appId: config.appId,
      accessToken: maskSalesforceSecret(config.accessToken),
      endpoint: config.appEndpoint,
      contactKey: state.contactKey ?? "(não definido — sync após login pendente)",
      entregaPush: "MobilePush direto (APNs/FCM)",
      pushEnabled: state.pushEnabled,
      osNotificationGranted,
      deviceId: state.deviceId ?? "(indisponível)",
      systemToken: systemToken ? maskSalesforceSecret(systemToken) : "(indisponível)",
      tags: state.tags ?? [],
      analyticsEnabled: SALESFORCE_SDK_CONFIG.analyticsEnabled,
    });

    if (!osNotificationGranted) {
      sfLog.warn(
        "Notificações do SO desativadas — no painel SFMC aparece 'Device is not opted in'. " +
          "Ative em Ajustes do aparelho ou reinstale o app para ver o prompt novamente."
      );
    }

    if (!state.contactKey) {
      sfLog.warn(
        "Contact key ainda não definido — eventos podem não aparecer no painel SFMC até o login sincronizar o usuário"
      );
    }

    if (!state.pushEnabled) {
      sfLog.warn("Push desabilitado no dispositivo — notificações SFMC podem não funcionar");
    }

    // Dump nativo por último: aparece no console do Xcode com o device token e o
    // registro que o SFMC enviou ao MobilePush.
    await this.logNativeSdkState();
  }

  private async waitForNativePlugin(timeoutMs = 8000): Promise<void> {
    if (this.getMcPlugin()) {
      return;
    }

    if (typeof document === "undefined") {
      sfLog.warn("document indisponível — não foi possível aguardar deviceready");
      return;
    }

    sfLog.info(`Aguardando MCCordovaPlugin (timeout ${timeoutMs}ms)...`);

    await new Promise<void>((resolve) => {
      const finish = () => resolve();
      const timer = setTimeout(() => {
        if (!this.getMcPlugin()) {
          sfLog.warn("Timeout aguardando MCCordovaPlugin — plugin ainda indisponível");
        }
        finish();
      }, timeoutMs);
      document.addEventListener(
        "deviceready",
        () => {
          clearTimeout(timer);
          if (this.getMcPlugin()) {
            sfLog.ok("MCCordovaPlugin disponível após deviceready");
          } else {
            sfLog.warn("deviceready disparou, mas MCCordovaPlugin ainda indisponível");
          }
          finish();
        },
        { once: true }
      );
    });
  }

  private toWebAttributes(attributes: ContactAttributes): SalesforceWebAttributes {
    const out: SalesforceWebAttributes = {};
    for (const [key, value] of Object.entries(attributes)) {
      if (value === undefined || value === null) continue;
      out[key] = typeof value === "boolean" || typeof value === "number" ? value : String(value);
    }
    return out;
  }

  private toNativeEventAttributes(attributes: ContactAttributes): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(attributes)) {
      // Eventos sf_* usam string vazia para garantir a presença de campos Text
      // sem valor no schema. Omitimos somente valores que não podem ser serializados.
      if (value === undefined || value === null) continue;
      out[key] = String(value);
    }
    return out;
  }

  private callMcPlugin<T>(
    invoke: (resolve: (value: T) => void) => void,
    fallback: T,
    optional = false
  ): Promise<T> {
    return new Promise((resolve) => {
      try {
        invoke(resolve);
      } catch (error) {
        if (!optional) {
          sfLog.error("Erro na chamada nativa do plugin", error);
        }
        resolve(fallback);
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
